import { showToast, openErrorModal } from '@vario-connect/frontend-design';
import { callFn, escapeHtml, escapeAttr, banner } from './mapping-helpers.js';

// ============================================================================
// Gambio-Lister - Vorschau-Tab
//
// Ruft gambio-listing-preview auf und zeigt:
//  - fieldPreview-Tabelle: Feld · Modus · aufgelöster Wert · auto-Badge
//  - errors als v-banner-danger
//  - warnings als v-banner-warn
//  - "Aktualisieren"-Button für Reload
// ============================================================================

export interface PreviewTabCtx {
  supabaseUrl: string;
  session: { token: string };
}

export interface PreviewTabArgs {
  listingId: string;
}

interface FieldPreviewRow {
  key: string;
  label: string;
  sourceType: string;
  resolved: string | null;
  managed: boolean;
}

interface PreviewResponse {
  preview: Record<string, unknown> | null;
  fieldPreview: FieldPreviewRow[];
  errors: string[];
  warnings: string[];
  meta?: Record<string, unknown>;
}

export function renderListPreview(
  host: HTMLElement,
  ctx: PreviewTabCtx,
  args: PreviewTabArgs,
): void {
  mount(host, ctx, args);
}

function mount(host: HTMLElement, ctx: PreviewTabCtx, args: PreviewTabArgs): void {
  host.innerHTML = `
    <div class="v-pa-md" id="gpreview-root">
      <div class="v-row v-row-sm" style="justify-content:space-between;align-items:center;margin-bottom:12px;">
        <h4 class="klst-detail-section" style="margin:0;">Vorschau</h4>
        <button type="button" class="v-button" id="gpreview-refresh">
          <span class="v-icon fa-regular fa-rotate"></span> Aktualisieren
        </button>
      </div>
      <div id="gpreview-body">
        <div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Vorschau…</div>
      </div>
    </div>`;

  host.querySelector<HTMLButtonElement>('#gpreview-refresh')?.addEventListener('click', () => {
    void load(host, ctx, args);
  });

  void load(host, ctx, args);
}

async function load(host: HTMLElement, ctx: PreviewTabCtx, args: PreviewTabArgs): Promise<void> {
  const body = host.querySelector<HTMLElement>('#gpreview-body');
  if (!body) return;

  body.innerHTML = `<div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Vorschau…</div>`;

  let data: PreviewResponse;
  try {
    data = await callFn<PreviewResponse>(ctx, 'gambio-listing-preview', {
      listingId: args.listingId,
    });
  } catch (e) {
    body.innerHTML = banner('error', `Vorschau konnte nicht geladen werden: ${(e as Error).message}`);
    openErrorModal({
      title: 'Vorschau nicht ladbar',
      items: [{ message: (e as Error).message }],
      context: { action: 'gambio-listing-preview', listingId: args.listingId },
      json: e,
    });
    return;
  }

  body.innerHTML = renderPreview(data);
}

function renderPreview(data: PreviewResponse): string {
  const parts: string[] = [];

  // Error banners
  for (const err of data.errors ?? []) {
    parts.push(`<div class="v-banner v-banner-danger" style="margin-bottom:8px;">
      <span class="v-icon fa-light fa-circle-xmark"></span>
      <div>${escapeHtml(err)}</div>
    </div>`);
  }

  // Warning banners
  for (const warn of data.warnings ?? []) {
    parts.push(`<div class="v-banner v-banner-warn" style="margin-bottom:8px;">
      <span class="v-icon fa-light fa-triangle-exclamation"></span>
      <div>${escapeHtml(warn)}</div>
    </div>`);
  }

  const rows = data.fieldPreview ?? [];

  if (!rows.length && !data.errors?.length && !data.warnings?.length) {
    parts.push(`<div class="v-banner v-banner-info">
      <span class="v-icon fa-light fa-circle-info"></span>
      <div>Keine Vorschau-Daten verfügbar - bitte zuerst ein Mapping anlegen.</div>
    </div>`);
    return parts.join('');
  }

  if (rows.length) {
    const rowsHtml = rows
      .map((row) => {
        const sourceLabel = SOURCE_TYPE_LABEL[row.sourceType] ?? escapeHtml(row.sourceType);
        const resolvedHtml = row.resolved != null
          ? `<span style="font-family:monospace;font-size:12px;">${escapeHtml(String(row.resolved))}</span>`
          : `<span class="v-text-muted">—</span>`;
        const autoBadge = row.managed
          ? `<span class="v-pill" style="font-size:10px;margin-left:4px;">auto</span>`
          : '';
        return `
          <tr>
            <td style="padding:5px 8px 5px 0;vertical-align:top;">
              <div><strong>${escapeHtml(row.label)}</strong>${autoBadge}</div>
              <div class="v-text-muted" style="font-size:11px;">${escapeHtml(row.key)}</div>
            </td>
            <td style="padding:5px 8px;vertical-align:top;white-space:nowrap;">
              <span class="v-pill" style="font-size:11px;">${sourceLabel}</span>
            </td>
            <td style="padding:5px 0 5px 8px;vertical-align:top;max-width:320px;overflow-wrap:break-word;">
              ${resolvedHtml}
            </td>
          </tr>`;
      })
      .join('');

    parts.push(`
      <div style="overflow-x:auto;">
        <table style="width:100%;border-collapse:collapse;font-size:13px;">
          <thead>
            <tr style="border-bottom:2px solid var(--go-border,#e5e7eb);text-align:left;">
              <th style="padding:4px 8px 4px 0;font-weight:600;color:var(--go-text-muted,#6b7280);">Feld</th>
              <th style="padding:4px 8px;font-weight:600;color:var(--go-text-muted,#6b7280);">Modus</th>
              <th style="padding:4px 0 4px 8px;font-weight:600;color:var(--go-text-muted,#6b7280);">Aufgelöster Wert</th>
            </tr>
          </thead>
          <tbody style="border-top:1px solid var(--go-border,#e5e7eb);">
            ${rowsHtml}
          </tbody>
        </table>
      </div>`);
  }

  return parts.join('');
}

const SOURCE_TYPE_LABEL: Record<string, string> = {
  unmapped:    'Nicht gemappt',
  vario_path:  'Vario-Feld',
  fixed_value: 'Fester Wert',
  template:    'Formel',
  managed:     'Auto-Sync',
};
